import React, { FormEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ErrorCode } from 'react-dropzone';
import { useNavigate } from 'react-router-dom';
import {
  Form,
  FormErrorTypes,
  Input,
  Modal,
  useFormState,
  useFormValidation,
  FileUpload,
  googlePlaceToAddress,
} from 'dash-ui';
import type { IOnUploadOptionsProps } from 'dash-ui';
import {
  isUrlValid,
  DashValidation,
  FileModel,
  isEmptyOrNil,
  isNameLengthValid,
} from 'dash-common';
import {
  GetOrganizationByIdDocument,
  GetOrganizationByIdQuery,
  GetOrganizationsDocument,
  GetOrganizationsForSelectDocument,
  useCreateOrganizationMutation,
  useEditOrganizationMutation,
} from 'gql/generated.graphql';
import useDraftFileUpload from 'common/hooks/useDraftFileUpload';
import { RoutesPath } from 'flow-components/routes/routes';
import LocationSection from 'pages/organizations/OrganizationDetailsPage/components/LocationSection/LocationSection';

enum FormFields {
  Name = 'name',
  Url = 'url',
  Place = 'place',
  Logo = 'logo',
}

const FormFieldsList = [
  FormFields.Name,
  FormFields.Url,
  FormFields.Place,
  FormFields.Logo,
];

interface IProps {
  organization?: GetOrganizationByIdQuery['getOrganizationById'];
  isOpen: boolean;
  onClose: () => void;
}

function SaveOrganizationModal({ organization, isOpen, onClose }: IProps) {
  const { t } = useTranslation('forms');
  const { t: tCommonLabels } = useTranslation('common', {
    keyPrefix: 'labels',
  });
  const { t: tPages } = useTranslation('pages', {
    keyPrefix: 'organisationPage',
  });
  const { t: errorSlugs, i18n: i18nErrorSlugs } = useTranslation('common', {
    keyPrefix: 'serverMessages',
  });
  const navigate = useNavigate();
  const formRef = useRef<HTMLFormElement>(null);
  const [
    formErrors,
    ,
    [setNameError, setUrlError, , setLogoError],
    [resetFormErrors],
  ] = useFormValidation(FormFieldsList);
  const [
    formState,
    [setName, setUrl, setAddress, setLogo],
    [formOnSubmit, formOnError, , setFormValues, resetForm],
  ] = useFormState(FormFieldsList);

  const draftFileUpload = useDraftFileUpload();
  const [isLogoLoading, setIsLogoLoading] = useState(false);
  const [editOrganizationMutation, { loading }] = useEditOrganizationMutation();
  const [createOrganizationMutation, { loading: createLoading }] =
    useCreateOrganizationMutation({
      refetchQueries: [
        { query: GetOrganizationsDocument, fetchPolicy: 'network-only' },
        GetOrganizationsForSelectDocument,
      ],
    });

  // set initial data state for update
  useEffect(() => {
    // FIXME: re-creating context functions everytime state change. Use useCallback
    if (organization) {
      const { name, website, location, logoFile } = organization || {};

      setFormValues({
        name,
        url: website,
        place: {
          placeId: location?.googlePlaceId,
          lat: location?.geoLocation.coordinates[1],
          lng: location?.geoLocation.coordinates[0],
        },
        logo: logoFile?._id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organization]);

  const validName = (value: string) => {
    if (isEmptyOrNil(value)) {
      setNameError({
        type: FormErrorTypes.Invalid,
        message: t`messages.nameBlank`,
      });
      return false;
    }
    if (!isNameLengthValid(value)) {
      setNameError({
        type: FormErrorTypes.Invalid,
        message: t('forms:messages.maxCharacterLimit', {
          number: DashValidation.MaxNameLength.toString(),
        }),
      });
      return false;
    }
    setNameError(null);
    return true;
  };
  const validUrl = (value: string) => {
    if (value && !isUrlValid(value)) {
      setUrlError({
        type: FormErrorTypes.Invalid,
        message: t`messages.urlInvalid`,
      });
      return false;
    }
    setUrlError(null);
    return true;
  };
  const onValueTextChange =
    (fieldName: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      switch (fieldName) {
        case FormFields.Name: {
          setName(e.target.value);
          validName(e.target.value);
          break;
        }
        case FormFields.Url: {
          setUrl(e.target.value);
          validUrl(e.target.value);
          break;
        }
      }
    };

  const onFileChanged = (fileId: string) => {
    setLogo(fileId || null);
    setLogoError(null);
    setIsLogoLoading(false);
  };

  const onFileUpload = (file: File, options: IOnUploadOptionsProps) => {
    setLogoError(null);
    setIsLogoLoading(true);
    draftFileUpload(file, options);
  };

  const onModalClose = () => {
    resetForm();
    resetFormErrors();
    onClose();
  };

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    event.stopPropagation();
    formOnSubmit();
    const { name, url, logo } = formState;
    if ([validName(name), validUrl(url)].filter((v) => !v).length) {
      return null;
    }

    const organizationData = {
      name,
      locationInput: Object.values(formState.place || {}).filter(Boolean).length
        ? {
            ...(organization?.locationId && {
              locationId: organization.locationId,
            }),
            googlePlaceId: formState.place.placeId,
            lat: formState.place.lat,
            lng: formState.place.lng,
            ...googlePlaceToAddress(formState.place),
          }
        : null,
      website: url,
      logoFileId: logo,
    };

    if (organization) {
      return editOrganizationMutation({
        variables: {
          organizationId: organization?._id,
          input: organizationData,
        },
        refetchQueries: [
          { query: GetOrganizationsDocument, fetchPolicy: 'network-only' },
          {
            query: GetOrganizationByIdDocument,
            variables: { organizationId: organization._id },
            fetchPolicy: 'network-only',
          },
        ],
      })
        .then(onModalClose)
        .catch((error) => {
          const errorS = i18nErrorSlugs.exists(error.message)
            ? errorSlugs(error.message)
            : errorSlugs`unknown`;
          formOnError(errorS || '');
        });
    }

    createOrganizationMutation({
      variables: {
        input: organizationData,
      },
    })
      .then(({ data }) => {
        const organizationId = data?.createOrganization._id || '';
        onModalClose();
        if (organizationId) {
          navigate(
            RoutesPath.OrganizationDetails.replace(':id', organizationId)
          );
        }
      })
      .catch((error) => {
        const errorS = i18nErrorSlugs.exists(error.message)
          ? errorSlugs(error.message)
          : errorSlugs`unknown`;
        formOnError(errorS || '');
      });
    return null;
  };

  return (
    <Modal
      title={
        tPages(
          `${
            organization
              ? 'editOrganizationModalLabel'
              : 'addOrganisationModalLabel'
          }`
        ) || ''
      }
      isOpen={isOpen}
      onRequestClose={onModalClose}
      submitButton={{
        title: organization ? tCommonLabels`update` : tCommonLabels`create`,
        isLoading: loading || createLoading,
        disabled: loading || isLogoLoading || createLoading,
        onClick: () => {
          if (formRef.current) {
            formRef.current.requestSubmit();
          }
        },
      }}
      cancelButton={{ title: tCommonLabels`cancel`, onClick: onModalClose }}
    >
      <Form ref={formRef} onSubmit={onSubmit}>
        <Form.Group mt={1}>
          <Form.Label required>{t`labels.name`}</Form.Label>
          <Input
            w="100%"
            value={formState.name || ''}
            placeholder={t`placeholders.organizationName` || ''}
            onChange={onValueTextChange('name')}
            $errorType={formErrors[FormFields.Name]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Name]?.type}>
            {formErrors[FormFields.Name]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group mt={1}>
          <Form.Label>{t`labels.url`}</Form.Label>
          <Input
            w="100%"
            value={formState.url || ''}
            placeholder={t`placeholders.url` || ''}
            onChange={onValueTextChange('url')}
            $errorType={formErrors[FormFields.Url]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Url]?.type}>
            {formErrors[FormFields.Url]?.message}
          </Form.Feedback>
        </Form.Group>
        <LocationSection address={formState.place} setAddress={setAddress} />
        <Form.Group mt={1}>
          <Form.Label>{t`labels.logo`}</Form.Label>
          <FileUpload.Single
            initialFile={(organization?.logoFile as FileModel) || null}
            accept={{ 'image/png': ['.png'], 'image/jpg': ['.jpg', '.jpeg'] }}
            onUpload={onFileUpload}
            onChanged={onFileChanged}
            maxSize={DashValidation.MaxLogoFileSize}
            onDropRejected={(error) => {
              let message = 'forms:messages:uploadErrors:unknown';
              if (error.code === ErrorCode.FileInvalidType) {
                message = 'forms:messages:uploadErrors:fileInvalidType';
              }
              if (error.code === ErrorCode.FileTooLarge) {
                message = 'forms:messages:uploadErrors:fileTooLarge';
              }
              setLogoError({
                type: FormErrorTypes.Invalid,
                message: t(message, {
                  type: '.png, .jpg, .jpeg',
                  maxSize: DashValidation.MaxLogoFileSize / 1024 / 1024,
                }),
              });
            }}
            $errorType={formErrors[FormFields.Logo]?.type}
          />
          <Form.Feedback $errorType={formErrors[FormFields.Logo]?.type}>
            {formErrors[FormFields.Logo]?.message}
          </Form.Feedback>
        </Form.Group>
        <Form.Group mt={4}>
          <Form.Feedback $errorType={FormErrorTypes.Invalid}>
            {formState.errorSlug}
          </Form.Feedback>
        </Form.Group>
      </Form>
    </Modal>
  );
}

export default SaveOrganizationModal;
